import {
  AbstractControl,
  UntypedFormGroup,
  ValueChangeEvent,
} from '@angular/forms';
import { filter, Observable, takeUntil } from 'rxjs';

/**
 * Shared helpers for typeaheads whose kendo control is bound by `[value]`/`(valueChange)` instead
 * of `[formGroup]`/`[formControlName]` (pre-commit 13d). Used by both web typeahead bases, which
 * extend different shared bases and so cannot share these as inherited members.
 */

/**
 * Task 35874: the value to hand an object-mode (`[valuePrimitive]="false"`) kendo list. While a
 * saved scalar id (for example an HRRef under `valueProperty`) waits for its lookup, the
 * selection holds that raw scalar, and kendo logs "Expected value of type Object" on every
 * change-detection pass. Show the placeholder until the row resolves instead.
 */
export function typeaheadViewItem<T>(item: T | null | undefined): T | null {
  return item != null && typeof item === 'object' ? item : null;
}

/**
 * Bug 34085: kendo's placeholder row (`[defaultItem]="{ FullName: config?.placeholder }"`) is a
 * pickable item. Picking it handed the form `{ FullName: placeholder }`, a truthy object, so a
 * required field passed while the host read no id and saved it blank. A pick of that row is a clear.
 */
export function typeaheadPickOrNull<T>(
  item: T,
  placeholder: string | null | undefined
): T {
  if (item == null || typeof item !== 'object' || Array.isArray(item)) {
    return item;
  }
  const values = Object.values(item);
  // The selection type has no null member, though a cleared typeahead carries null everywhere.
  return values.length === 1 && values[0] === placeholder ? (null as T) : item;
}

/** The typeahead's form control, or undefined before config/group are set. */
export function typeaheadViewControl(
  config: { formControlName?: string } | null | undefined,
  group: UntypedFormGroup | null | undefined
): AbstractControl | undefined {
  const name = config?.formControlName;
  return name ? group?.controls?.[name] : undefined;
}

/** The ng-* status classes `[formControlName]` (NgControlStatus) used to put on the kendo control. */
export interface TypeaheadViewStatus {
  invalid: boolean;
  valid: boolean;
  touched: boolean;
}

/**
 * PR 5721 review: with no control directive on a `[value]`-bound kendo control, nothing puts
 * ng-invalid/ng-valid/ng-touched on it, so the _forms.scss #34403 rule stops marking a required
 * field that is still empty. Mirror NgControlStatus: a disabled control is neither valid nor
 * invalid.
 */
export function typeaheadViewStatus(
  control: AbstractControl | undefined
): TypeaheadViewStatus {
  return {
    invalid: !!control?.invalid,
    valid: !!control?.valid,
    touched: !!control?.touched,
  };
}

/**
 * Re-derive the display on EVERY write to the form control (show/hide clearing a field, a reset,
 * a patch from another control), not only picks, prefill and auto-fill, so the dropdown shows what
 * the form holds, as `[formControlName]` did.
 */
export function bindTypeaheadViewToControl(
  control: AbstractControl | undefined,
  destroy$: Observable<unknown>,
  rederive: () => void,
  repaint: () => void
) {
  control?.valueChanges.pipe(takeUntil(destroy$)).subscribe(() => rederive());
  // Status and touched changes (a submit marking all touched, a sibling-driven validator) carry
  // no value change, but the mirrored ng-* classes must still repaint under OnPush.
  control?.events
    .pipe(
      filter((e) => !(e instanceof ValueChangeEvent)),
      takeUntil(destroy$)
    )
    .subscribe(() => repaint());
}

/**
 * A kendo-multiselect's (valueChange) hands its whole selection. valueChangeItem is typed for one option, but the
 * core base's _setSelectedItem keeps an array whole on a multi-select, so the array is passed on as before (WI 34037).
 */
export function passMultiSelectValueChange<T>(
  host: { valueChangeItem(item: T): void },
  items: T[]
): void {
  host.valueChangeItem(items as unknown as T);
}
